/**
 * Tracks the last Stripe checkout this browser started but didn't complete so
 * ResumeCheckoutBanner can offer a one-click way back in. localStorage only —
 * the server-side recovery email (checkout.session.expired webhook) covers
 * the cross-device case.
 */
export type PendingCheckout = {
  kind: "subscription" | "trial";
  /** Full server plan key, e.g. "plus_monthly" (subscription kind only). */
  planId?: string;
  /** Display label, e.g. "Plus Monthly". */
  planName?: string;
  price?: number;
  startedAt: number;
  dismissedAt?: number;
};

const STORAGE_KEY = "solmi_pending_checkout";
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export function savePendingCheckout(
  checkout: Omit<PendingCheckout, "startedAt" | "dismissedAt">,
): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...checkout, startedAt: Date.now() }),
    );
  } catch {
    // Storage unavailable (private mode / quota) — banner just won't show.
  }
}

export function readPendingCheckout(): PendingCheckout | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingCheckout;
    if (!parsed || typeof parsed.startedAt !== "number") return null;
    if (Date.now() - parsed.startedAt > MAX_AGE_MS) {
      clearPendingCheckout();
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function dismissPendingCheckout(): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as PendingCheckout;
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...parsed, dismissedAt: Date.now() }),
    );
  } catch {
    // ignore
  }
}

export function clearPendingCheckout(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
